import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";
const HTTP_UNAUTHORIZED = 401;
const IS_DEV = process.env.NODE_ENV !== "production";

function devWarn(...args) {
  if (IS_DEV) console.warn(...args);
}

/**
 * useAdminIdeas — private working-folder CRUD for story/essay ideas.
 * Kinds: story | essay | either.
 * Statuses: backlog | selected | drafting | shelved | merged.
 */
export default function useAdminIdeas() {
  const [key] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const [ideas, setIdeas] = useState([]);
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const authHeaders = () => ({ "X-Admin-Key": key });

  const fetchIdeas = useCallback(async () => {
    if (!key) return;
    setStatus({ state: "loading", error: "" });
    try {
      const res = await axios.get(`${API}/admin/ideas`, {
        headers: authHeaders(),
      });
      setIdeas(res.data || []);
      setStatus({ state: "ready", error: "" });
    } catch (err) {
      if (err?.response?.status === HTTP_UNAUTHORIZED) {
        setStatus({ state: "unauthorized", error: "Session expired." });
      } else {
        setStatus({ state: "error", error: "Could not load ideas." });
      }
    }
  }, [key]);

  useEffect(() => {
    if (key) fetchIdeas();
  }, [key, fetchIdeas]);

  const createIdea = useCallback(
    async (payload) => {
      const res = await axios.post(`${API}/admin/ideas`, payload, {
        headers: authHeaders(),
      });
      setIdeas((prev) => [res.data, ...prev]);
      return res.data;
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const getIdea = useCallback(
    async (id) => {
      const res = await axios.get(`${API}/admin/ideas/${id}`, {
        headers: authHeaders(),
      });
      return res.data;
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const patchIdea = useCallback(
    async (id, patch) => {
      const res = await axios.patch(`${API}/admin/ideas/${id}`, patch, {
        headers: authHeaders(),
      });
      setIdeas((prev) => prev.map((i) => (i.id === id ? res.data : i)));
      return res.data;
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const deleteIdea = useCallback(
    async (id) => {
      try {
        await axios.delete(`${API}/admin/ideas/${id}`, {
          headers: authHeaders(),
        });
        setIdeas((prev) => prev.filter((i) => i.id !== id));
        return true;
      } catch (err) {
        devWarn("[VBB] deleteIdea failed", err);
        return false;
      }
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const promoteToEssay = useCallback(
    async (id) => {
      const res = await axios.post(
        `${API}/admin/ideas/${id}/promote`,
        {},
        { headers: authHeaders() }
      );
      // Refresh idea in local list (status becomes 'drafting', essay_id set)
      const refreshed = await getIdea(id);
      setIdeas((prev) => prev.map((i) => (i.id === id ? refreshed : i)));
      return res.data; // the new Essay
    },
    [key, getIdea]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const findSimilar = useCallback(
    async (id, limit = 3) => {
      const res = await axios.get(`${API}/admin/ideas/${id}/similar`, {
        params: { limit },
        headers: authHeaders(),
      });
      return res.data; // [{ idea, score }, ...]
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const mergeInto = useCallback(
    async (sourceId, targetId) => {
      const res = await axios.post(
        `${API}/admin/ideas/${sourceId}/merge`,
        { target_id: targetId },
        { headers: authHeaders() }
      );
      // Refresh both the source (now merged) and target in local list.
      setIdeas((prev) =>
        prev.map((i) => {
          if (i.id === targetId) return res.data;
          if (i.id === sourceId) {
            return { ...i, status: "merged", merged_into_id: targetId };
          }
          return i;
        })
      );
      return res.data; // updated target
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const unmerge = useCallback(
    async (id) => {
      const res = await axios.post(
        `${API}/admin/ideas/${id}/unmerge`,
        {},
        { headers: authHeaders() }
      );
      setIdeas((prev) => prev.map((i) => (i.id === id ? res.data : i)));
      return res.data;
    },
    [key]  // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    key,
    ideas,
    status,
    fetchIdeas,
    createIdea,
    getIdea,
    patchIdea,
    deleteIdea,
    promoteToEssay,
    findSimilar,
    mergeInto,
    unmerge,
  };
}
